import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PixelFactoryContext } from 'pixel-base'
import {
  cancelFinanceInvoice,
  confirmFinanceInvoice,
  deleteFinanceInvoice,
  getFinanceInvoice,
  postFinanceInvoice,
  reverseFinanceInvoice,
} from 'utils/api-routes-accounts'
import FinanceDocumentActions from '../../components/domain/FinanceDocumentActions'
import FinanceDocumentSummary, {
  asStatusNode,
} from '../../components/domain/FinanceDocumentSummary'
import FinanceLineItemsTable from '../../components/domain/FinanceLineItemsTable'
import FinancePageShell from '../../components/FinancePageShell'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { Invoice } from '../../types/finance'
import formatFinanceDate from '../../utils/formatFinanceDate'

const InvoiceDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const invoice = useFinanceObservable<Invoice>(() => getFinanceInvoice(id), {
    immediate: Boolean(id),
  })
  const row = invoice.data
  const state = (row?.state || 'draft').toLowerCase()
  const canEdit = ['draft', 'confirmed'].includes(state)
  const canConfirm = state === 'draft'
  const canPost = state === 'confirmed'
  const canReverse = ['posted', 'partially_paid', 'paid'].includes(state)
  const canCancel = ['draft', 'confirmed'].includes(state)
  const canDelete = ['draft', 'confirmed'].includes(state)

  const runAction = (request, successMessage: string, goBack = false) => {
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(successMessage)
        if (goBack) navigate('/finance/invoices')
        else invoice.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Invoice action failed'),
    })
  }

  return (
    <FinancePageShell
      title={row?.number || `Invoice #${id}`}
      breadcrumbs={[
        { name: 'Finance', active: false },
        { name: 'Invoices', active: false },
        { name: row?.number || `Invoice #${id}`, active: true },
      ]}
      isLoading={invoice.loading}
    >
      {row && (
        <div style={{ display: 'grid', gap: 20 }}>
          <FinanceDocumentSummary
            title="Invoice Summary"
            status={asStatusNode(row.state)}
            fields={[
              {
                label: 'Partner',
                value: row.partner?.name || row.partner_id || '--',
              },
              {
                label: 'Journal',
                value: row.journal?.name || row.journal_id || '--',
              },
              {
                label: 'Document Date',
                value: formatFinanceDate(row.document_date),
              },
              {
                label: 'Posting Date',
                value: formatFinanceDate(row.posting_date),
              },
              { label: 'Due Date', value: formatFinanceDate(row.due_date) },
              {
                label: 'Total',
                value: row.amount_total ?? '--',
                emphasis: true,
              },
              {
                label: 'Residual',
                value: row.amount_residual ?? '--',
                emphasis: true,
              },
              { label: 'Posted Move', value: row.journal_entry_id || '--' },
            ]}
          />
          <FinanceLineItemsTable variant="invoice" rows={row.lines || []} />
          <FinanceDocumentActions
            actions={[
              {
                key: 'back',
                label: 'Back To Edit',
                tone: 'secondary',
                hidden: !canEdit,
                onClick: () => navigate('/finance/invoices'),
              },
              {
                key: 'confirm',
                label: 'Confirm',
                tone: 'primary',
                hidden: !canConfirm,
                onClick: () =>
                  runAction(confirmFinanceInvoice(row.id), 'Invoice confirmed'),
              },
              {
                key: 'post',
                label: 'Post',
                tone: 'success',
                hidden: !canPost,
                onClick: () =>
                  runAction(
                    postFinanceInvoice(row.id, {
                      idempotency_key: `invoice-post-${row.id}`,
                    }),
                    'Invoice posted',
                  ),
              },
              {
                key: 'reverse',
                label: 'Reverse',
                tone: 'warning',
                hidden: !canReverse,
                onClick: () =>
                  runAction(
                    reverseFinanceInvoice(row.id, {
                      idempotency_key: `invoice-reverse-${row.id}`,
                    }),
                    'Invoice reversed',
                  ),
              },
              {
                key: 'cancel',
                label: 'Cancel',
                tone: 'danger',
                hidden: !canCancel,
                onClick: () =>
                  runAction(cancelFinanceInvoice(row.id), 'Invoice cancelled'),
              },
              {
                key: 'delete',
                label: 'Delete',
                tone: 'danger',
                hidden: !canDelete,
                onClick: () =>
                  runAction(
                    deleteFinanceInvoice(row.id),
                    'Invoice deleted',
                    true,
                  ),
              },
            ]}
          />
        </div>
      )}
    </FinancePageShell>
  )
}

export default InvoiceDetail
